/**
 * BottomNavBar - Frosted glass bottom navigation
 *
 * Semi-transparent background + backdrop-blur.
 * Frosted glass effect becomes visible when page content scrolls behind the nav.
 */

import type { ComponentType } from 'react'
import type { LucideProps } from 'lucide-react'
import { useT } from '@/contexts/I18nContext'

export interface NavItem {
  id: string
  icon: ComponentType<LucideProps>
  label: string
}

export interface BottomNavBarProps {
  items: NavItem[]
  active: string
  onNavigate?: (id: string) => void
  className?: string
}

export function BottomNavBar({ items, active, onNavigate, className: _className }: BottomNavBarProps) { // eslint-disable-line @typescript-eslint/no-unused-vars
  const t = useT()
  return (
    <nav
      className="fixed bottom-0 left-0 w-full z-50 bg-canvas/95 backdrop-blur-xl rounded-t-xl shadow-subtle"
      role="navigation"
      aria-label={t('nav.mainNav')}
    >
      <div className="flex justify-around items-center px-4 py-2 pb-safe">
        {items.map((item) => {
          const isActive = active === item.id
          const Icon = item.icon
          return (
            <button
              key={item.id}
              onClick={() => onNavigate?.(item.id)}
              className={
                'flex flex-col items-center justify-center transition-all duration-200 active:scale-95 ' +
                (isActive
                  ? 'bg-primary/10 text-primary rounded-full px-6 py-2'
                  : 'text-muted px-6 py-2')
              }
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="w-5 h-5 mb-1" strokeWidth={isActive ? 2.5 : 1.5} />
              <span className="text-[12px] font-semibold tracking-wide">{item.label}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}
